import React, { useState, useEffect, useMemo, useRef } from "react";
import type { LanguageCode } from "../customerlanguages";
import { useGetCustomerQuery } from "../../api/customerApi";
import { FileDrop } from "../../components/FileDrop";
import { Button, Dropdown, Icon } from "../../components/tailwind";
import type { Option } from "../../components/tailwind/dropdown/Dropdown";
import { TranslationMemoryPaginationBar } from "../../machine-translation/manage/translation-memory/TranslationMemoryPaginationBar";

type GlossaryEntry = {
  id: number;
  source_text: string;
  target_text: string;
  comment: string;
  category: string;
};

type GlossaryLanguage = {
  value: string;
  name: string;
  short_name: string;
  flag: string;
  iso: string;
};

type NewEntryRow = {
  row_id: number;
  source_text: string;
  target_text: string;
  comment: string;
  category: string;
};

type EntryField = "source_text" | "target_text" | "comment" | "category";

const RemoveRowButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Button
    type="button"
    variant="secondary-alt"
    icon
    compact
    className="tw-absolute tw-right-0 tw-top-1/2 -tw-translate-y-1/2"
    aria-label="Remove row"
    data-testid="GlossaryForm-remove-row"
    onClick={onClick}
  >
    <Icon name="delete_outline" />
  </Button>
);

const ROW_STYLE: React.CSSProperties = { paddingRight: "3rem" };

type Props = {
  customerLanguages: string;
  sourceLanguage: string;
  targetLanguage: string;
  formAction: string;
  token: string;
};

export const GlossaryForm: React.FC<Props> = ({
  customerLanguages,
  sourceLanguage,
  targetLanguage,
  formAction,
}) => {
  const { isLoading: isCustomerLoading } = useGetCustomerQuery();
  const languages: GlossaryLanguage[] = JSON.parse(customerLanguages);

  const [selectedSourceLanguage] = useState<GlossaryLanguage>(
    JSON.parse(sourceLanguage)
  );
  const [selectedTargetLanguage] = useState<GlossaryLanguage>(
    JSON.parse(targetLanguage)
  );

  const [fetchedEntries, setFetchedEntries] = useState<GlossaryEntry[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [newEntryRows, setNewEntryRows] = useState<NewEntryRow[]>([
    { row_id: 0, source_text: "", target_text: "", comment: "", category: "" },
  ]);
  const [nextNewRowId, setNextNewRowId] = useState(1);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const filteredEntries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return fetchedEntries;
    return fetchedEntries.filter(
      (e) =>
        e.source_text.toLowerCase().includes(q) ||
        e.target_text.toLowerCase().includes(q) ||
        e.comment.toLowerCase().includes(q) ||
        e.category.toLowerCase().includes(q)
    );
  }, [fetchedEntries, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredEntries.length / pageSize));
  const pageStart = (currentPage - 1) * pageSize;
  const pageEnd = pageStart + pageSize;
  const visibleEntries = filteredEntries.slice(pageStart, pageEnd);
  const entriesForSubmit = useMemo(
    () => [...fetchedEntries].sort((a, b) => a.id - b.id),
    [fetchedEntries]
  );

  const downloadUrl = `${formAction.replace(/\/$/, "")}/download.csv`;
  const downloadDisabled = fetchedEntries.length === 0;

  const navigateToLanguagePair = (
    source: GlossaryLanguage,
    target: GlossaryLanguage
  ): void => {
    const parts = formAction.replace(/\/$/, "").split("/");
    parts[parts.length - 1] = target.value;
    parts[parts.length - 2] = source.value;
    window.location.href = parts.join("/");
  };

  const handleSourceLanguageChange = (iso: LanguageCode): void => {
    const lang = languages.find((l) => l.iso === iso);
    if (!lang) return;
    navigateToLanguagePair(lang, selectedTargetLanguage);
  };

  const handleTargetLanguageChange = (iso: LanguageCode): void => {
    const lang = languages.find((l) => l.iso === iso);
    if (!lang) return;
    navigateToLanguagePair(selectedSourceLanguage, lang);
  };

  const sourceLanguageOptions = useMemo((): Option<LanguageCode>[] => {
    return languages.map((l) => ({
      key: l.iso,
      value: l.iso as LanguageCode,
      text: `${l.flag} ${l.short_name} (${l.iso})`,
      disabled: l.iso === selectedTargetLanguage.iso,
    }));
  }, [languages, selectedTargetLanguage.iso]);

  const targetLanguageOptions = useMemo((): Option<LanguageCode>[] => {
    return languages.map((l) => ({
      key: l.iso,
      value: l.iso as LanguageCode,
      text: `${l.flag} ${l.short_name} (${l.iso})`,
      disabled: l.iso === selectedSourceLanguage.iso,
    }));
  }, [languages, selectedSourceLanguage.iso]);

  const handleEntryChange = (
    id: number,
    field: EntryField,
    value: string
  ): void => {
    setFetchedEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, [field]: value } : e))
    );
  };

  const handleEntryRemove = (id: number): void => {
    setFetchedEntries((prev) => prev.filter((e) => e.id !== id));
  };

  const handleNewRowRemove = (rowId: number): void => {
    setNewEntryRows((prev) => prev.filter((row) => row.row_id !== rowId));
  };

  const handleNewRowChange = (
    index: number,
    field: EntryField,
    value: string
  ): void => {
    setNewEntryRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  useEffect(() => {
    if (selectedFile && fileInputRef.current) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(selectedFile);
      fileInputRef.current.files = dataTransfer.files;
    } else if (!selectedFile && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [selectedFile]);

  useEffect(() => {
    const fetchEntries = async (): Promise<void> => {
      setIsFetching(true);
      try {
        const url = `${formAction.replace(/\/$/, "")}/entries.json`;
        const response = await fetch(url);
        if (!response.ok) throw new Error("Failed to fetch entries");
        const data = await response.json();
        setFetchedEntries(data.entries);
      } catch (error) {
        console.error("Failed to fetch glossary entries:", error);
      } finally {
        setIsFetching(false);
      }
    };
    fetchEntries();
  }, [formAction]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <form className="txu-form" method="post" encType="multipart/form-data">
      {entriesForSubmit.map((e) => (
        <React.Fragment key={e.id}>
          <input type="hidden" name="sources" value={e.source_text} />
          <input type="hidden" name="targets" value={e.target_text} />
          <input type="hidden" name="comments" value={e.comment} />
          <input type="hidden" name="categories" value={e.category} />
        </React.Fragment>
      ))}

      <header className="tw-flex tw-justify-between tw-items-center">
        <h1 className="tw-flex tw-items-center tw-gap-2 !tw-m-0 !tw-p-0 !tw-leading-none">
          <span>Glossary:</span>{" "}
          <span>
            {selectedSourceLanguage.flag} {selectedSourceLanguage.short_name}
          </span>{" "}
          <Icon name="arrow_forward" className="tw-text-gray-500" />{" "}
          <span>
            {selectedTargetLanguage.flag} {selectedTargetLanguage.short_name}
          </span>
        </h1>
        <div className="tw-flex tw-items-center tw-gap-6">
          <Button
            type="submit"
            variant="primary"
            icon
            data-testid="GlossaryForm-save"
          >
            <Icon name="save" />
            Save
          </Button>
        </div>
      </header>

      <details className="txu-accordion">
        <summary className="tw-flex tw-items-center">
          <Icon name="description" className="tw-text-gray-800 tw-mr-2" />{" "}
          Import glossary from file
        </summary>
        <div className="tw-flex tw-flex-col tw-gap-4">
          <p className="tw-text-gray-500">
            Select a file to upload glossary entries in batch. <br />
            File must be either CSV or Excel (.xlsx). No header should be
            included. Columns are read in this order: source, target, category,
            comments. Only source and target are required.
          </p>
          <div>
            <FileDrop
              selectedFile={selectedFile}
              isInvalid={false}
              onChange={(file): void => setSelectedFile(file)}
            />
            <input
              ref={fileInputRef}
              type="file"
              name="uploaded_file"
              style={{ display: "none" }}
            />
          </div>
          {fetchedEntries.length > 0 && (
            <label className="tw-w-full !tw-flex tw-items-center tw-justify-center tw-gap-1">
              <input
                className="txu-input"
                type="checkbox"
                name="merge"
                value="1"
              />
              <span className="tw-uppercase tw-font-medium">
                Merge with existing items (below)
              </span>
            </label>
          )}
        </div>
      </details>

      <div className="txu-divider"></div>

      <article>
        <h3 className="tw-m-0">Glossary entries:</h3>
        <p className="tw-text-gray-500 tw-text-sm tw-m-0">
          Add or edit glossary entries below
        </p>

        <div className="tw-grid tw-grid-cols-2 tw-gap-12 tw-my-4">
          <div className="tw-flex tw-items-center tw-gap-4">
            <label className="txu-label !tw-m-0">Source Language:</label>
            <div className="tw-w-1/2 tw-min-w-0">
              <Dropdown<LanguageCode>
                size="sm"
                disabled={isCustomerLoading}
                options={sourceLanguageOptions}
                placeholder="Select source language"
                value={selectedSourceLanguage.iso as LanguageCode}
                onChange={(value): void => {
                  if (value == null || Array.isArray(value)) return;
                  handleSourceLanguageChange(value);
                }}
                testIdBase="glossary-source-language"
              />
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-gap-4">
            <label className="txu-label !tw-m-0">Target Language:</label>
            <div className="tw-w-1/2 tw-min-w-0">
              <Dropdown<LanguageCode>
                size="sm"
                disabled={isCustomerLoading}
                options={targetLanguageOptions}
                placeholder="Select target language"
                value={selectedTargetLanguage.iso as LanguageCode}
                onChange={(value): void => {
                  if (value == null || Array.isArray(value)) return;
                  handleTargetLanguageChange(value);
                }}
                testIdBase="glossary-target-language"
              />
            </div>
            <a
              href={downloadDisabled ? undefined : downloadUrl}
              className={`txu-btn txu-secondary-alt tw-text-center ${
                downloadDisabled ? "tw-pointer-events-none tw-opacity-50" : ""
              }`}
              aria-disabled={downloadDisabled}
              data-testid="GlossaryForm-download-csv"
            >
              Download as CSV
            </a>
          </div>
        </div>

        <div className="tw-my-4">
          <input
            type="text"
            className="txu-input tw-w-full"
            placeholder="Search entries..."
            value={searchQuery}
            data-testid="GlossaryForm-search"
            onChange={(e): void => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="tw-my-4 tw-text-gray-500 tw-text-lg">
          Results: {filteredEntries.length} entries found
        </div>

        <TranslationMemoryPaginationBar
          pageStart={pageStart}
          pageEnd={Math.min(pageEnd, filteredEntries.length)}
          totalEntryCount={filteredEntries.length}
          pageSize={pageSize}
          onPageSizeChange={(size): void => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          currentPage={currentPage}
          totalPages={totalPages}
          onGoPrev={(): void => setCurrentPage((p) => Math.max(1, p - 1))}
          onGoNext={(): void =>
            setCurrentPage((p) => Math.min(totalPages, p + 1))
          }
          pageSizeSelectId="glossary-page-size-top"
        />

        <div
          className="tw-grid tw-grid-cols-4 tw-gap-4 tw-mt-4"
          style={ROW_STYLE}
        >
          <label className="txu-label">
            {selectedSourceLanguage.flag} {selectedSourceLanguage.short_name}
            <span className="tw-text-gray-500 tw-ml-2">
              ({selectedSourceLanguage.iso})
            </span>
          </label>
          <label className="txu-label">
            {selectedTargetLanguage.flag} {selectedTargetLanguage.short_name}
            <span className="tw-text-gray-500 tw-ml-2">
              ({selectedTargetLanguage.iso})
            </span>
          </label>
          <label className="txu-label">Category</label>
          <label className="txu-label">Comment</label>
        </div>

        <div className="tw-flex tw-flex-col">
          {isFetching && (
            <p className="tw-order-2 tw-text-gray-500 tw-py-4">
              Loading entries...
            </p>
          )}

          {!isFetching &&
            visibleEntries.map((entry) => (
              <div
                key={entry.id}
                className="tw-order-2 tw-grid tw-grid-cols-4 tw-gap-4 tw-relative"
                style={ROW_STYLE}
              >
                <input
                  type="text"
                  className="txu-input tw-my-1"
                  value={entry.source_text}
                  data-testid="GlossaryForm-source"
                  onChange={(e): void =>
                    handleEntryChange(entry.id, "source_text", e.target.value)
                  }
                />
                <input
                  type="text"
                  className="txu-input tw-my-1"
                  value={entry.target_text}
                  data-testid="GlossaryForm-target"
                  onChange={(e): void =>
                    handleEntryChange(entry.id, "target_text", e.target.value)
                  }
                />
                <input
                  type="text"
                  className="txu-input tw-my-1"
                  value={entry.category}
                  data-testid="GlossaryForm-category"
                  onChange={(e): void =>
                    handleEntryChange(entry.id, "category", e.target.value)
                  }
                />
                <input
                  type="text"
                  className="txu-input tw-my-1"
                  value={entry.comment}
                  data-testid="GlossaryForm-comment"
                  onChange={(e): void =>
                    handleEntryChange(entry.id, "comment", e.target.value)
                  }
                />
                <RemoveRowButton
                  onClick={(): void => handleEntryRemove(entry.id)}
                />
              </div>
            ))}

          <div className="tw-order-first tw-mb-4">
            {newEntryRows.map((row, index) => (
              <div
                key={row.row_id}
                className="tw-grid tw-grid-cols-4 tw-gap-4 tw-relative"
                style={ROW_STYLE}
              >
                <input
                  type="text"
                  className="txu-input tw-my-1"
                  placeholder="Source term"
                  name="sources"
                  value={row.source_text}
                  data-testid="GlossaryForm-new-source"
                  onChange={(e): void =>
                    handleNewRowChange(index, "source_text", e.target.value)
                  }
                />
                <input
                  type="text"
                  className="txu-input tw-my-1"
                  placeholder="Target term"
                  name="targets"
                  value={row.target_text}
                  data-testid="GlossaryForm-new-target"
                  onChange={(e): void =>
                    handleNewRowChange(index, "target_text", e.target.value)
                  }
                />
                <input
                  type="text"
                  className="txu-input tw-my-1"
                  placeholder="Category"
                  name="categories"
                  value={row.category}
                  data-testid="GlossaryForm-new-category"
                  onChange={(e): void =>
                    handleNewRowChange(index, "category", e.target.value)
                  }
                />
                <input
                  type="text"
                  className="txu-input tw-my-1"
                  placeholder="Comment"
                  name="comments"
                  value={row.comment}
                  data-testid="GlossaryForm-new-comment"
                  onChange={(e): void =>
                    handleNewRowChange(index, "comment", e.target.value)
                  }
                />
                <RemoveRowButton
                  onClick={(): void => handleNewRowRemove(row.row_id)}
                />
              </div>
            ))}
            <div className="tw-flex tw-justify-end tw-mt-2">
              <Button
                type="button"
                content="Add row"
                variant="primary-alt"
                onClick={(): void => {
                  setNewEntryRows((prev) => [
                    {
                      row_id: nextNewRowId,
                      source_text: "",
                      target_text: "",
                      comment: "",
                      category: "",
                    },
                    ...prev,
                  ]);
                  setNextNewRowId((prev) => prev + 1);
                }}
              />
            </div>
          </div>
        </div>

        <TranslationMemoryPaginationBar
          pageStart={pageStart}
          pageEnd={Math.min(pageEnd, filteredEntries.length)}
          totalEntryCount={filteredEntries.length}
          pageSize={pageSize}
          onPageSizeChange={(size): void => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          currentPage={currentPage}
          totalPages={totalPages}
          onGoPrev={(): void => setCurrentPage((p) => Math.max(1, p - 1))}
          onGoNext={(): void =>
            setCurrentPage((p) => Math.min(totalPages, p + 1))
          }
          pageSizeSelectId="glossary-page-size-bottom"
          className="tw-flex tw-items-center tw-justify-between tw-mt-3"
        />
      </article>
    </form>
  );
};
